import { EvaluationWorkspace } from '../evaluations/EvaluationWorkspace'
import { evaluationCapabilities } from '../evaluations/EvaluationHub'
import { useModalLifecycle } from '../shared/useModalLifecycle'
/**
 * Team Spain v0 — Modal Detalle de Evento, Convocatoria (RSVP), Agenda y Sesiones (M2)
 */

import React, { useState, useEffect } from 'react'
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Users,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Lock,
  Plus,
  Trash2,
  Copy,
  Layers,
  AlertCircle,
  Edit2,
  Radio,
  Trophy,
  BarChart2,
} from 'lucide-react'
import {
  EventItem,
  EventParticipant,
  CampActivity,
  TrainingSession,
  AttendanceStatus,
} from '../../types/calendar'
import { calendarService } from './calendarService'
import { sportsService } from '../sports/sportsService'
import { ScriptEditorModal } from '../sports/ScriptEditorModal'
import { FieldCaptureEntryModal } from '../sports/FieldCaptureEntryModal'
import { FieldConsoleView } from '../sports/FieldConsoleView'
import { TeamBlockCapture, FieldSnap, CaptureWithSnapCount } from '../../types/fieldCapture'
import { fieldCaptureService } from '../sports/fieldCaptureService'
import { SnapHistoryModal } from '../sports/SnapHistoryModal'
import { PastSnapDetailModal } from '../sports/PastSnapDetailModal'
import { useAuth } from '../auth/AuthContext'
import { hasOperation } from '../auth/permissions'
import { ManageRosterModal } from './ManageRosterModal'

const toDatetimeLocal = (isoStr?: string): string => {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const min = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`
}

export type EventDetailTabKey = 'details' | 'rsvp' | 'agenda' | 'sessions' | 'matches' | 'evaluations'

interface EventDetailModalProps {
  eventId: string
  onClose: () => void
  onEventUpdated?: () => void
  initialTab?: EventDetailTabKey
}

export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  eventId,
  onClose,
  onEventUpdated,
  initialTab = 'details',
}) => {
  const { user } = useAuth()
  const [event, setEvent] = useState<EventItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<EventDetailTabKey>(initialTab)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Sub-estados
  const [participants, setParticipants] = useState<EventParticipant[]>([])
  const [activities, setActivities] = useState<CampActivity[]>([])
  const [sessions, setSessions] = useState<TrainingSession[]>([])
  const [childMatches, setChildMatches] = useState<EventItem[]>([])
  const [teamCaptures, setTeamCaptures] = useState<CaptureWithSnapCount[]>([])
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [selectedHistoryCapture, setSelectedHistoryCapture] = useState<TeamBlockCapture | null>(null)
  const [historySnaps, setHistorySnaps] = useState<FieldSnap[]>([])
  const [pastSnapDetailOpen, setPastSnapDetailOpen] = useState(false)
  const [selectedDetailSnap, setSelectedDetailSnap] = useState<FieldSnap | null>(null)
  const [loadingHistoryId, setLoadingHistoryId] = useState<string | null>(null)

  // Mi RSVP
  const [myStatus, setMyStatus] = useState<AttendanceStatus>('pending')
  const [myComment, setMyComment] = useState('')
  const [isSubmittingRsvp, setIsSubmittingRsvp] = useState(false)

  // Filtro de convocados para staff
  const [rsvpFilter, setRsvpFilter] = useState<'all' | 'confirmed' | 'pending' | 'declined' | 'withdrawn'>('all')

  // Modal de Gestión de Convocatoria
  const [showManageRosterModal, setShowManageRosterModal] = useState(false)

  // Modificación de fechas del evento
  const [isEditingDates, setIsEditingDates] = useState(false)
  const [editStartTime, setEditStartTime] = useState('')
  const [editEndTime, setEditEndTime] = useState('')
  const [isSavingDates, setIsSavingDates] = useState(false)

  // Filtro de días en Agenda
  const [selectedAgendaDay, setSelectedAgendaDay] = useState<string>('all')

  // Cancelación
  const [showCancelPrompt, setShowCancelPrompt] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [isCancelling, setIsCancelling] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [isDuplicatingSession, setIsDuplicatingSession] = useState(false)

  // Formulario nueva actividad
  const [showAddActivity, setShowAddActivity] = useState(false)
  const [newActTitle, setNewActTitle] = useState('')
  const [newActType, setNewActType] = useState<CampActivity['activity_type']>('practice')
  const [newActStartTime, setNewActStartTime] = useState('')
  const [newActEndTime, setNewActEndTime] = useState('')
  const [newActLocation, setNewActLocation] = useState('')
  const [newActResponsibles, setNewActResponsibles] = useState('')
  const [newActUnit, setNewActUnit] = useState<CampActivity['target_unit']>('all')
  const [newActSharedNotes, setNewActSharedNotes] = useState('')
  const [newActInternalNotes, setNewActInternalNotes] = useState('')
  const [isSubmittingActivity, setIsSubmittingActivity] = useState(false)

  // Permisos
  const canEvaluations = user?.memberships.some(m=>m.is_active&&(!m.team_id||m.team_id===event?.team_id)&&(m.roles.includes('athlete')||evaluationCapabilities.some(c=>m.capabilities?.includes(c))))
  const isStaff = user?.memberships.some((m) => m.is_active && (!m.team_id || m.team_id === event?.team_id) && (hasOperation(m.roles, m.capabilities || [], 'manage_events') || m.roles.includes('medic')))
  const isCoachOrAdmin = user?.memberships.some((m) => m.is_active && (!m.team_id || m.team_id === (event?.team_id)) && hasOperation(m.roles, m.capabilities || [], 'manage_events'))
  const isCoach = user?.memberships.some((m) => m.is_active && (!m.team_id || m.team_id === (event?.team_id)) && hasOperation(m.roles, m.capabilities || [], 'capture_field'))

  // M3: Guion de repeticiones de Bloque Team
  const [selectedScriptTarget, setSelectedScriptTarget] = useState<{
    sessionId: string
    sessionTitle: string
    blockId: string
    blockTitle: string
    scriptId?: string | null
  } | null>(null)

  // M4: Consola de Captura en Campo para Bloque Team
  const [fieldCaptureModalTarget, setFieldCaptureModalTarget] = useState<{
    sessionId: string
    sessionTitle: string
    blockId: string
    blockTitle: string
    scriptId?: string | null
  } | null>(null)
  const [activeCaptureId, setActiveCaptureId] = useState<string | null>(null)

  const handleOpenTeamScript = async (sess: TrainingSession, blk: { id: string; title: string }) => {
    if (!event) return
    try {
      const existing = await sportsService.getPracticeScripts(event.team_id, { blockId: blk.id })
      const scriptId = existing.length > 0 ? existing[0].id : null
      setSelectedScriptTarget({
        sessionId: sess.id,
        sessionTitle: sess.title,
        blockId: blk.id,
        blockTitle: blk.title,
        scriptId,
      })
    } catch {
      setSelectedScriptTarget({
        sessionId: sess.id,
        sessionTitle: sess.title,
        blockId: blk.id,
        blockTitle: blk.title,
        scriptId: null,
      })
    }
  }

  const loadEventData = React.useCallback(async () => {
    setLoading(true)
    setErrorMsg(null)
    let ev: EventItem | null = null
    try {
      ev = await calendarService.getEventById(eventId)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar datos del evento.')
      setLoading(false)
      return
    }

    if (!ev) {
      setErrorMsg('El evento solicitado no existe o no tienes permisos para consultarlo.')
      setLoading(false)
      return
    }
    setEvent(ev)

    // Cargar sub-recursos en paralelo de forma aislada para que un fallo en uno no bloquee los demás
    const [partsRes, actsRes, sessRes, matchesRes, capturesRes] = await Promise.allSettled([
      calendarService.getEventParticipants(eventId),
      calendarService.getCampActivities(eventId),
      calendarService.getTrainingSessions(ev.team_id, eventId),
      calendarService.getChildEvents(eventId),
      fieldCaptureService.getAllCapturesWithSnapCount(ev.team_id),
    ])

    const partialWarnings: string[] = []

    if (partsRes.status === 'fulfilled') {
      setParticipants(partsRes.value)
      const myPart = partsRes.value.find((p) => p.user_id === user?.id)
      if (myPart) {
        setMyStatus(myPart.attendance_status)
        setMyComment(myPart.comment || '')
      }
    } else {
      console.error('Error al cargar participantes:', partsRes.reason)
      partialWarnings.push('participantes')
    }

    if (actsRes.status === 'fulfilled') {
      setActivities(actsRes.value)
    } else {
      console.error('Error al cargar agenda:', actsRes.reason)
      partialWarnings.push('actividades de agenda')
    }

    if (sessRes.status === 'fulfilled') {
      setSessions(sessRes.value)
    } else {
      console.error('Error al cargar sesiones:', sessRes.reason)
      partialWarnings.push('sesiones de entrenamiento')
    }

    if (matchesRes.status === 'fulfilled') {
      setChildMatches(matchesRes.value)
    }

    if (capturesRes.status === 'fulfilled') {
      setTeamCaptures(capturesRes.value)
    }

    if (partialWarnings.length > 0) {
      setErrorMsg(`Aviso: No se pudieron sincronizar algunos datos secundarios (${partialWarnings.join(', ')}).`)
    }

    setLoading(false)
  }, [eventId, user?.id])

  const handleOpenMatchHistory = async (cap: TeamBlockCapture) => {
    setLoadingHistoryId(cap.id)
    setErrorMsg(null)
    try {
      const details = await fieldCaptureService.getCaptureDetails(cap.id)
      setSelectedHistoryCapture(details.capture)
      setHistorySnaps(details.snaps)
      setHistoryModalOpen(true)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar historial de jugadas.')
    } finally {
      setLoadingHistoryId(null)
    }
  }

  useEffect(() => {
    loadEventData()
  }, [loadEventData])

  useModalLifecycle(true, () => {
    if (!showManageRosterModal && !selectedScriptTarget) onClose()
  })

  // Manejar respuesta RSVP propia
  const handleSaveMyRsvp = async () => {
    if (isSubmittingRsvp) return
    setIsSubmittingRsvp(true)
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      await calendarService.updateMyAttendance(eventId, myStatus, myComment)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al registrar tu asistencia.')
      setIsSubmittingRsvp(false)
      return
    }

    setSuccessMsg('Tu respuesta de asistencia ha sido registrada correctamente.')
    setIsSubmittingRsvp(false)

    try {
      await loadEventData()
      if (onEventUpdated) onEventUpdated()
    } catch (reloadErr) {
      console.error('Error al recargar tras RSVP:', reloadErr)
    }
  }

  // Publicar evento
  const handlePublish = async () => {
    if (!isCoachOrAdmin || isPublishing) return
    setIsPublishing(true)
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      await calendarService.publishEvent(eventId)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al publicar evento.')
      setIsPublishing(false)
      return
    }

    setSuccessMsg('Evento publicado con éxito. Ahora es visible para todos los convocados.')
    setIsPublishing(false)

    try {
      await loadEventData()
      if (onEventUpdated) onEventUpdated()
    } catch (reloadErr) {
      console.error('Error al recargar evento tras publicar:', reloadErr)
    }
  }

  // Cancelar evento
  const handleCancelEvent = async () => {
    if (!isCoachOrAdmin || !cancelReason.trim() || isCancelling) return
    setIsCancelling(true)
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      await calendarService.cancelEvent(eventId, cancelReason)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cancelar evento.')
      setIsCancelling(false)
      return
    }

    setSuccessMsg('Evento cancelado. Se ha registrado el motivo y conservado el histórico.')
    setShowCancelPrompt(false)
    setIsCancelling(false)

    try {
      await loadEventData()
      if (onEventUpdated) onEventUpdated()
    } catch (reloadErr) {
      console.error('Error al recargar evento tras cancelar:', reloadErr)
    }
  }

  // Añadir actividad de agenda
  const handleCreateActivity = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!event || !isCoachOrAdmin || isSubmittingActivity) return
    setIsSubmittingActivity(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    try {
      await calendarService.createCampActivity(
        {
          event_id: event.id,
          team_id: event.team_id,
          title: newActTitle,
          activity_type: newActType,
          start_time: newActStartTime,
          end_time: newActEndTime,
          location: newActLocation,
          responsibles: newActResponsibles,
          target_unit: newActUnit,
          shared_notes: newActSharedNotes,
        },
        newActInternalNotes
      )
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al añadir actividad.')
      setIsSubmittingActivity(false)
      return
    }

    // Escritura en base de datos completada con éxito
    setSuccessMsg('Actividad agregada a la agenda correctamente.')
    setShowAddActivity(false)
    setNewActTitle('')
    setNewActStartTime('')
    setNewActEndTime('')
    setNewActLocation('')
    setNewActResponsibles('')
    setNewActSharedNotes('')
    setNewActInternalNotes('')
    setIsSubmittingActivity(false)

    // Recarga de pantalla separada
    try {
      await loadEventData()
    } catch (reloadErr) {
      console.error('Error al recargar agenda tras creación:', reloadErr)
      setErrorMsg('La actividad se guardó con éxito en el servidor, pero hubo un problema al actualizar la pantalla.')
    }
  }

  // Eliminar actividad
  const handleDeleteActivity = async (actId: string) => {
    if (!confirm('¿Seguro que deseas eliminar esta actividad de la agenda?')) return
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      await calendarService.deleteCampActivity(actId)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al eliminar actividad.')
      return
    }

    setSuccessMsg('Actividad eliminada de la agenda.')
    try {
      await loadEventData()
    } catch (reloadErr) {
      console.error('Error al recargar tras eliminar actividad:', reloadErr)
    }
  }

  // Duplicar sesión de entrenamiento
  const handleDuplicateSession = async (sessId: string) => {
    if (!isCoachOrAdmin || !event || isDuplicatingSession) return
    const newDate = prompt(
      'Introduce la fecha para la nueva sesión (YYYY-MM-DD):',
      event.start_time.split('T')[0]
    )
    if (!newDate) return

    setIsDuplicatingSession(true)
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      await calendarService.duplicateTrainingSession(sessId, newDate)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al duplicar sesión.')
      setIsDuplicatingSession(false)
      return
    }

    setSuccessMsg('Sesión de entrenamiento y bloques duplicados correctamente.')
    setIsDuplicatingSession(false)

    try {
      await loadEventData()
    } catch (reloadErr) {
      console.error('Error al recargar tras duplicar sesión:', reloadErr)
    }
  }

  // Detectar solapamientos en actividades
  const checkOverlaps = (acts: CampActivity[]): boolean => {
    for (let i = 0; i < acts.length; i++) {
      for (let j = i + 1; j < acts.length; j++) {
        const a = acts[i]
        const b = acts[j]
        const startA = new Date(a.start_time).getTime()
        const endA = new Date(a.end_time).getTime()
        const startB = new Date(b.start_time).getTime()
        const endB = new Date(b.end_time).getTime()

        // Si solapan en tiempo
        if (startA < endB && startB < endA) {
          return true
        }
      }
    }
    return false
  }

  const hasOverlaps = checkOverlaps(activities)

  // Días del evento para selector en Agenda
  const eventDays = React.useMemo(() => {
    if (!event) return []
    const days: Array<{ dateStr: string; label: string }> = []
    const start = new Date(event.start_time)
    const end = new Date(event.end_time)

    const curr = new Date(start)
    curr.setHours(0, 0, 0, 0)
    const endDay = new Date(end)
    endDay.setHours(0, 0, 0, 0)

    let dayIdx = 1
    while (curr <= endDay) {
      const yyyy = curr.getFullYear()
      const mm = String(curr.getMonth() + 1).padStart(2, '0')
      const dd = String(curr.getDate()).padStart(2, '0')
      const dateStr = `${yyyy}-${mm}-${dd}`
      const weekday = curr.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })
      days.push({
        dateStr,
        label: `Día ${dayIdx}: ${weekday}`,
      })
      curr.setDate(curr.getDate() + 1)
      dayIdx++
    }
    return days
  }, [event])

  // Filtrar actividades por día seleccionado
  const filteredActivities = React.useMemo(() => {
    if (selectedAgendaDay === 'all') return activities
    return activities.filter((act) => {
      const actDate = act.start_time.split('T')[0]
      const d = new Date(act.start_time)
      const pad = (n: number) => String(n).padStart(2, '0')
      const localDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      return actDate === selectedAgendaDay || localDate === selectedAgendaDay
    })
  }, [activities, selectedAgendaDay])

  // Conflictos de actividades si se cambian las fechas del evento
  const dateChangeConflicts = React.useMemo(() => {
    if (!isEditingDates || !editStartTime || !editEndTime) return []
    const newStart = new Date(editStartTime)
    const newEnd = new Date(editEndTime)

    return activities.filter((act) => {
      const actStart = new Date(act.start_time)
      const actEnd = new Date(act.end_time)
      return actStart < newStart || actEnd > newEnd
    })
  }, [isEditingDates, editStartTime, editEndTime, activities])

  // Validación en tiempo real de actividad fuera de intervalo
  const isActDateOutside = React.useMemo(() => {
    if (!event || !newActStartTime || !newActEndTime) return false
    const actStart = new Date(newActStartTime)
    const actEnd = new Date(newActEndTime)
    const evStart = new Date(event.start_time)
    const evEnd = new Date(event.end_time)
    return actStart < evStart || actEnd > evEnd || actEnd <= actStart
  }, [event, newActStartTime, newActEndTime])

  // Guardar nuevas fechas del evento
  const handleSaveEventDates = async () => {
    if (!event || !isCoachOrAdmin || isSavingDates) return
    if (new Date(editEndTime) <= new Date(editStartTime)) {
      setErrorMsg('La fecha y hora de fin del evento debe ser posterior a la de inicio.')
      return
    }

    if (dateChangeConflicts.length > 0) {
      setErrorMsg(
        `Existen ${dateChangeConflicts.length} actividad(es) fuera del nuevo intervalo. Debes resolverlas antes de guardar las fechas.`
      )
      return
    }

    setIsSavingDates(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    try {
      await calendarService.updateEvent(event.id, {
        start_time: new Date(editStartTime).toISOString(),
        end_time: new Date(editEndTime).toISOString(),
      })
      setSuccessMsg('Fechas del evento actualizadas correctamente.')
      setIsEditingDates(false)
      await loadEventData()
      if (onEventUpdated) onEventUpdated()
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al actualizar fechas del evento.')
    } finally {
      setIsSavingDates(false)
    }
  }

  // Apertura de añadir actividad con propuesta de día
  const handleToggleAddActivity = () => {
    if (!event) return
    if (!showAddActivity) {
      if (selectedAgendaDay !== 'all') {
        const defaultStart = `${selectedAgendaDay}T09:00`
        const defaultEnd = `${selectedAgendaDay}T10:00`
        const evStartLocal = toDatetimeLocal(event.start_time)
        const evEndLocal = toDatetimeLocal(event.end_time)

        setNewActStartTime(defaultStart < evStartLocal ? evStartLocal : defaultStart)
        setNewActEndTime(defaultEnd > evEndLocal ? evEndLocal : defaultEnd)
      } else {
        const evStartLocal = toDatetimeLocal(event.start_time)
        setNewActStartTime(evStartLocal)

        const evStartObj = new Date(event.start_time)
        evStartObj.setHours(evStartObj.getHours() + 1)
        const evStartPlusOne = toDatetimeLocal(evStartObj.toISOString())
        const evEndLocal = toDatetimeLocal(event.end_time)

        setNewActEndTime(evStartPlusOne > evEndLocal ? evEndLocal : evStartPlusOne)
      }
    }
    setShowAddActivity(!showAddActivity)
  }

  // Filtrar participantes
  const activeParticipants = participants.filter((p) => p.attendance_status !== 'withdrawn')
  const confirmedCount = participants.filter((p) => p.attendance_status === 'confirmed').length
  const pendingCount = participants.filter((p) => p.attendance_status === 'pending').length
  const declinedCount = participants.filter((p) => p.attendance_status === 'declined').length
  const withdrawnCount = participants.filter((p) => p.attendance_status === 'withdrawn').length

  const filteredParticipants = participants.filter((p) => {
    if (rsvpFilter === 'all') return p.attendance_status !== 'withdrawn'
    return p.attendance_status === rsvpFilter
  })

  const formatDateDisplay = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: event?.timezone || 'Europe/Madrid',
    })
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Encabezado */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <Calendar size={20} style={{ color: 'var(--brand-red)' }} />
            <div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
                {event?.title || 'Detalle del Evento'}
              </h2>
              {event && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.2rem' }}>
                  <span
                    className={`badge ${
                      event.status === 'published'
                        ? 'badge-success'
                        : event.status === 'cancelled'
                          ? 'badge-warning'
                          : 'badge-muted'
                    }`}
                  >
                    {event.status === 'published'
                      ? 'Publicado'
                      : event.status === 'cancelled'
                        ? 'Cancelado'
                        : 'Borrador'}
                  </span>
                  <span className="badge badge-red" style={{ textTransform: 'capitalize' }}>
                    {event.event_type === 'camp'
                      ? 'Concentración'
                      : event.event_type === 'practice'
                        ? 'Entrenamiento'
                        : event.event_type === 'game'
                          ? 'Partido'
                          : event.event_type === 'meeting'
                            ? 'Reunión'
                            : 'Evento'}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '0.4rem',
              color: 'var(--text-secondary)',
              borderRadius: 'var(--radius-sm)',
            }}
            aria-label="Cerrar modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Pestañas secundarias */}
        <div style={{ padding: '0 1.25rem', backgroundColor: 'var(--surface-card)' }}>
          <div className="subtabs-container" style={{ margin: 0 }}>
            {event?.event_type==='camp'&&canEvaluations&&<button className={`subtab-btn ${activeTab==='evaluations'?'active':''}`} onClick={()=>setActiveTab('evaluations')}>Evaluaciones</button>}
            <button
              type="button"
              className={`subtab-btn ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Información
            </button>
            <button
              type="button"
              className={`subtab-btn ${activeTab === 'rsvp' ? 'active' : ''}`}
              onClick={() => setActiveTab('rsvp')}
            >
              <Users size={15} />
              <span>Convocatoria ({participants.length})</span>
            </button>
            <button
              type="button"
              className={`subtab-btn ${activeTab === 'agenda' ? 'active' : ''}`}
              onClick={() => setActiveTab('agenda')}
            >
              <Clock size={15} />
              <span>Agenda ({activities.length})</span>
            </button>
            <button
              type="button"
              className={`subtab-btn ${activeTab === 'sessions' ? 'active' : ''}`}
              onClick={() => setActiveTab('sessions')}
            >
              <Layers size={15} />
              <span>Sesiones ({sessions.length})</span>
            </button>
            {childMatches.length > 0 && (
              <button
                type="button"
                className={`subtab-btn ${activeTab === 'matches' ? 'active' : ''}`}
                onClick={() => setActiveTab('matches')}
              >
                <Trophy size={15} />
                <span>Partidos ({childMatches.length})</span>
              </button>
            )}
          </div>
        </div>

        {/* Avisos de estado */}
        {errorMsg && (
          <div
            style={{
              margin: '0.75rem 1.25rem 0',
              padding: '0.65rem 0.85rem',
              backgroundColor: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)',
              border: '1px solid var(--status-danger-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.82rem',
            }}
          >
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div
            style={{
              margin: '0.75rem 1.25rem 0',
              padding: '0.65rem 0.85rem',
              backgroundColor: 'var(--status-success-bg)',
              color: 'var(--status-success-text)',
              border: '1px solid var(--status-success-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.82rem',
            }}
          >
            {successMsg}
          </div>
        )}

        {/* Cuerpo del Modal */}
        <div className="modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              Cargando información del evento...
            </div>
          ) : !event ? (
            <div className="empty-state">
              <AlertTriangle size={32} className="empty-state-icon" />
              <div className="empty-state-title">Evento no disponible</div>
              <div className="empty-state-text">No se pudo encontrar la información requerida.</div>
            </div>
          ) : (
            <>
              {activeTab==='evaluations'&&event.event_type==='camp'&&<EvaluationWorkspace teamId={event.team_id} campId={event.id}/>}
              {/* TAB 1: DETALLES */}
              {activeTab === 'details' && (
                <div>
                  {event.status === 'cancelled' && (
                    <div
                      style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: 'var(--status-warning-bg)',
                        color: 'var(--status-warning-text)',
                        border: '1px solid var(--status-warning-border)',
                        borderRadius: 'var(--radius-sm)',
                        marginBottom: '1rem',
                        fontSize: '0.84rem',
                      }}
                    >
                      <strong>Evento Cancelado:</strong>{' '}
                      {event.cancellation_reason || 'Sin motivo registrado.'}
                      {event.cancelled_at && (
                        <div style={{ fontSize: '0.76rem', marginTop: '0.25rem', opacity: 0.85 }}>
                          Cancelado el {formatDateDisplay(event.cancelled_at)}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                    <div style={{ padding: '0.75rem', backgroundColor: 'var(--surface-subtle)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--surface-border)' }}>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                        Horario de Inicio
                      </div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                        {formatDateDisplay(event.start_time)}
                      </div>
                    </div>

                    <div style={{ padding: '0.75rem', backgroundColor: 'var(--surface-subtle)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--surface-border)' }}>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                        Horario de Fin
                      </div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                        {formatDateDisplay(event.end_time)}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontSize: '0.82rem', fontWeight: 600 }}>
                      <MapPin size={15} />
                      <span>Ubicación</span>
                    </div>
                    <div style={{ fontSize: '0.92rem', color: 'var(--text-main)' }}>
                      {event.location}
                    </div>
                  </div>

                  {event.description && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ color: 'var(--text-secondary)', marginBottom: '0.25rem', fontSize: '0.82rem', fontWeight: 600 }}>
                        Descripción
                      </div>
                      <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {event.description}
                      </div>
                    </div>
                  )}

                  {/* Partidos Asociados (Concentración) */}
                  {childMatches.length > 0 && (
                    <div
                      style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: 'rgba(30, 58, 138, 0.04)',
                        border: '1px solid rgba(30, 58, 138, 0.15)',
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 700, fontSize: '0.92rem', color: 'var(--brand-blue, #1e3a8a)' }}>
                          <Trophy size={17} />
                          <span>Partidos Oficiales / Amistosos Asociados ({childMatches.length})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setActiveTab('matches')}
                          className="btn btn-outline"
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                        >
                          Ver todos los partidos
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {childMatches.map((m) => {
                          const cap = teamCaptures.find((c) => c.event_id === m.id)
                          const snapCount = cap?.snapCount ?? cap?.snap_count ?? 0
                          return (
                            <div
                              key={m.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.65rem 0.85rem',
                                backgroundColor: '#ffffff',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid #e2e8f0',
                                flexWrap: 'wrap',
                                gap: '0.5rem',
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.86rem', color: 'var(--text-main)' }}>{m.title}</div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  {new Date(m.start_time).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} • {m.location}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {cap && (
                                  <span className="badge badge-blue" style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem' }}>
                                    {snapCount} snaps
                                  </span>
                                )}
                                {cap && snapCount > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenMatchHistory(cap)}
                                    className="btn btn-secondary"
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                  >
                                    Ver snaps
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Acciones para Staff */}
                  {isCoachOrAdmin && (
                    <div
                      style={{
                        padding: '1rem',
                        backgroundColor: 'var(--surface-subtle)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--surface-border)',
                      }}
                    >
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.65rem' }}>
                        Gestión Técnica del Evento
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.65rem' }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            if (!isEditingDates) {
                              setEditStartTime(toDatetimeLocal(event.start_time))
                              setEditEndTime(toDatetimeLocal(event.end_time))
                            }
                            setIsEditingDates(!isEditingDates)
                          }}
                          style={{ fontSize: '0.82rem' }}
                        >
                          <Edit2 size={15} />
                          <span>{isEditingDates ? 'Cerrar edición de fechas' : 'Modificar fechas del evento'}</span>
                        </button>

                        {event.status === 'draft' && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handlePublish}
                            style={{ fontSize: '0.82rem' }}
                          >
                            <CheckCircle2 size={15} />
                            <span>Publicar Evento</span>
                          </button>
                        )}

                        {event.status !== 'cancelled' && (
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => setShowCancelPrompt(true)}
                            style={{ fontSize: '0.82rem' }}
                          >
                            <XCircle size={15} />
                            <span>Cancelar Evento</span>
                          </button>
                        )}
                      </div>

                      {/* Panel de edición de fechas con detección de conflictos */}
                      {isEditingDates && (
                        <div
                          style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            backgroundColor: 'var(--surface-card)',
                            border: '1px solid var(--surface-border)',
                            borderRadius: 'var(--radius-sm)',
                          }}
                        >
                          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.75rem' }}>
                            Modificar Horario de la Concentración
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '0.85rem' }}>
                            <div>
                              <label className="form-label">Nuevo Inicio</label>
                              <input
                                type="datetime-local"
                                className="form-input"
                                value={editStartTime}
                                onChange={(e) => setEditStartTime(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="form-label">Nuevo Fin</label>
                              <input
                                type="datetime-local"
                                className="form-input"
                                value={editEndTime}
                                onChange={(e) => setEditEndTime(e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Aviso de actividades en conflicto */}
                          {dateChangeConflicts.length > 0 && (
                            <div
                              style={{
                                padding: '0.75rem 0.85rem',
                                backgroundColor: 'var(--status-danger-bg)',
                                color: 'var(--status-danger-text)',
                                border: '1px solid var(--status-danger-border)',
                                borderRadius: 'var(--radius-sm)',
                                marginBottom: '0.85rem',
                                fontSize: '0.82rem',
                              }}
                            >
                              <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
                                <AlertTriangle size={16} />
                                <span>No se pueden guardar las fechas: {dateChangeConflicts.length} actividad(es) quedarían fuera del intervalo:</span>
                              </div>
                              <ul style={{ margin: '0.35rem 0 0.5rem 1.25rem', padding: 0 }}>
                                {dateChangeConflicts.map((c) => (
                                  <li key={c.id}>
                                    <strong>{c.title}</strong> ({formatDateDisplay(c.start_time)} a {formatDateDisplay(c.end_time)})
                                  </li>
                                ))}
                              </ul>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <span>
                                  Debes ajustar estas actividades en la <strong>Agenda</strong> antes de guardar. Las actividades no se borran ni mueven automáticamente.
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={() => setActiveTab('agenda')}
                                  style={{ fontSize: '0.76rem', padding: '0.25rem 0.5rem' }}
                                >
                                  Ir a Agenda
                                </button>
                              </div>
                            </div>
                          )}

                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setIsEditingDates(false)}
                              disabled={isSavingDates}
                              style={{ fontSize: '0.8rem' }}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleSaveEventDates}
                              disabled={isSavingDates || dateChangeConflicts.length > 0 || !editStartTime || !editEndTime}
                              style={{ fontSize: '0.8rem' }}
                            >
                              {isSavingDates ? 'Guardando...' : 'Guardar nuevas fechas'}
                            </button>
                          </div>
                        </div>
                      )}

                      {showCancelPrompt && (
                        <div style={{ marginTop: '1rem', padding: '0.85rem', backgroundColor: 'var(--surface-card)', border: '1px solid var(--status-danger-border)', borderRadius: 'var(--radius-sm)' }}>
                          <label className="form-label" style={{ fontSize: '0.82rem' }}>
                            Motivo de la cancelación (obligatorio):
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Ej. Coincidencia con jornada de liga o inclemencias meteorológicas"
                            style={{ marginBottom: '0.65rem' }}
                          />
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setShowCancelPrompt(false)}
                              style={{ fontSize: '0.78rem' }}
                            >
                              Volver
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={handleCancelEvent}
                              style={{ fontSize: '0.78rem' }}
                              disabled={!cancelReason.trim()}
                            >
                              Confirmar Cancelación
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* TAB 2: CONVOCATORIA Y ASISTENCIA (RSVP) */}
              {activeTab === 'rsvp' && (
                <div>
                  {/* Mi respuesta interactiva */}
                  <div
                    style={{
                      padding: '1rem',
                      backgroundColor: 'var(--surface-subtle)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--surface-border)',
                      marginBottom: '1.5rem',
                    }}
                  >
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.5rem' }}>
                      Mi Confirmación de Asistencia (RSVP)
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={() => setMyStatus('confirmed')}
                        style={{
                          padding: '0.5rem 0.85rem',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          border: myStatus === 'confirmed' ? '2px solid var(--status-success-text)' : '1px solid var(--surface-border)',
                          backgroundColor: myStatus === 'confirmed' ? 'var(--status-success-bg)' : 'var(--surface-card)',
                          color: myStatus === 'confirmed' ? 'var(--status-success-text)' : 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        ✓ Asistiré (Confirmada)
                      </button>
                      <button
                        type="button"
                        onClick={() => setMyStatus('pending')}
                        style={{
                          padding: '0.5rem 0.85rem',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          border: myStatus === 'pending' ? '2px solid var(--status-warning-text)' : '1px solid var(--surface-border)',
                          backgroundColor: myStatus === 'pending' ? 'var(--status-warning-bg)' : 'var(--surface-card)',
                          color: myStatus === 'pending' ? 'var(--status-warning-text)' : 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        ⏱ Pendiente de confirmar
                      </button>
                      <button
                        type="button"
                        onClick={() => setMyStatus('declined')}
                        style={{
                          padding: '0.5rem 0.85rem',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          border: myStatus === 'declined' ? '2px solid var(--status-danger-text)' : '1px solid var(--surface-border)',
                          backgroundColor: myStatus === 'declined' ? 'var(--status-danger-bg)' : 'var(--surface-card)',
                          color: myStatus === 'declined' ? 'var(--status-danger-text)' : 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        ✕ No puedo asistir
                      </button>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <input
                        type="text"
                        className="form-input"
                        value={myComment}
                        onChange={(e) => setMyComment(e.target.value)}
                        placeholder="Comentario opcional para el cuerpo técnico (ej. transporte, examen, viaje)"
                        style={{ fontSize: '0.82rem' }}
                      />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                        Visible para el staff y el interesado. No sustituye valoración médica.
                      </span>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveMyRsvp}
                        disabled={isSubmittingRsvp}
                        style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem', minHeight: '36px' }}
                      >
                        {isSubmittingRsvp ? 'Guardando...' : 'Guardar mi respuesta'}
                      </button>
                    </div>
                  </div>

                  {/* Resumen de Convocatoria para Staff */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.65rem', marginBottom: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => setRsvpFilter('all')}
                        className={`badge ${rsvpFilter === 'all' ? 'badge-muted' : ''}`}
                        style={{ cursor: 'pointer', fontSize: '0.78rem' }}
                      >
                        Activos ({activeParticipants.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setRsvpFilter('confirmed')}
                        className="badge badge-success"
                        style={{ cursor: 'pointer', fontSize: '0.78rem', opacity: rsvpFilter === 'confirmed' ? 1 : 0.6 }}
                      >
                        Confirmados ({confirmedCount})
                      </button>
                      <button
                        type="button"
                        onClick={() => setRsvpFilter('pending')}
                        className="badge badge-warning"
                        style={{ cursor: 'pointer', fontSize: '0.78rem', opacity: rsvpFilter === 'pending' ? 1 : 0.6 }}
                      >
                        Pendientes ({pendingCount})
                      </button>
                      <button
                        type="button"
                        onClick={() => setRsvpFilter('declined')}
                        className="badge badge-red"
                        style={{ cursor: 'pointer', fontSize: '0.78rem', opacity: rsvpFilter === 'declined' ? 1 : 0.6 }}
                      >
                        No asisten ({declinedCount})
                      </button>
                      {withdrawnCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setRsvpFilter('withdrawn')}
                          className="badge badge-muted"
                          style={{ cursor: 'pointer', fontSize: '0.78rem', opacity: rsvpFilter === 'withdrawn' ? 1 : 0.6 }}
                        >
                          Retirados ({withdrawnCount})
                        </button>
                      )}
                    </div>

                    {/* Botón Acción Visible: Gestionar Convocatoria */}
                    {isCoachOrAdmin && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setShowManageRosterModal(true)}
                        style={{ fontSize: '0.82rem', padding: '0.45rem 0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem', minHeight: '36px' }}
                      >
                        <Users size={16} />
                        <span>Gestionar convocatoria</span>
                      </button>
                    )}
                  </div>

                  {/* Lista de Convocados */}
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Participante</th>
                          <th>Rol / Posición</th>
                          <th>Estado</th>
                          <th>Comentario</th>
                          {isCoachOrAdmin && <th style={{ width: '100px' }}>Acción</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredParticipants.map((part) => (
                          <tr key={part.id}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <span style={{ fontWeight: 600 }}>{part.athlete_name}</span>
                                {part.athlete_id && !part.user_id && (
                                  <span
                                    className="badge badge-muted"
                                    title="Atleta en el roster sin cuenta registrada de usuario"
                                    style={{ fontSize: '0.68rem', padding: '0.1rem 0.35rem', backgroundColor: 'rgba(100, 116, 139, 0.15)' }}
                                  >
                                    Sin cuenta
                                  </span>
                                )}
                              </div>
                              {part.user_email && (
                                <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                                  {part.user_email}
                                </div>
                              )}
                            </td>
                            <td>
                              <span className="badge badge-muted" style={{ textTransform: 'uppercase' }}>
                                {part.role}
                              </span>
                              {part.athlete_jersey && (
                                <span style={{ marginLeft: '0.35rem', fontWeight: 600, fontSize: '0.8rem' }}>
                                  #{part.athlete_jersey} {part.athlete_position}
                                </span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  part.attendance_status === 'confirmed'
                                    ? 'badge-success'
                                    : part.attendance_status === 'declined'
                                      ? 'badge-red'
                                      : part.attendance_status === 'withdrawn'
                                        ? 'badge-muted'
                                        : 'badge-warning'
                                }`}
                              >
                                {part.attendance_status === 'confirmed'
                                  ? 'Confirmada'
                                  : part.attendance_status === 'declined'
                                    ? 'No asiste'
                                    : part.attendance_status === 'withdrawn'
                                      ? 'Retirado'
                                      : 'Pendiente'}
                              </span>
                            </td>
                            <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                              {part.comment || '—'}
                            </td>
                            {isCoachOrAdmin && (
                              <td>
                                <select
                                  style={{
                                    fontSize: '0.75rem',
                                    padding: '0.2rem',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--surface-border)',
                                  }}
                                  value={part.attendance_status}
                                  onChange={async (e) => {
                                    const newStatus = e.target.value as AttendanceStatus
                                    try {
                                      await calendarService.updateParticipantAttendanceByStaff(
                                        part.id,
                                        newStatus
                                      )
                                      setSuccessMsg('Asistencia actualizada.')
                                    } catch (err: unknown) {
                                      setErrorMsg(err instanceof Error ? err.message : 'Error al actualizar asistencia del participante.')
                                      return
                                    }

                                    try {
                                      await loadEventData()
                                    } catch (reloadErr) {
                                      console.error('Error al recargar tras actualizar asistencia:', reloadErr)
                                    }
                                  }}
                                >
                                  <option value="confirmed">Confirmada</option>
                                  <option value="pending">Pendiente</option>
                                  <option value="declined">No asiste</option>
                                  <option value="withdrawn">Retirado</option>
                                </select>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TAB 3: AGENDA Y CRONOGRAMA */}
              {activeTab === 'agenda' && (
                <div>
                  {hasOverlaps && (
                    <div
                      style={{
                        padding: '0.65rem 0.85rem',
                        backgroundColor: 'var(--status-info-bg)',
                        color: 'var(--status-info-text)',
                        border: '1px solid var(--status-info-border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.8rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                      <span>
                        Aviso: Existen actividades simultáneas programadas en el horario (permitido para reuniones por unidades o sesiones paralelas de ataque y defensa).
                      </span>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.65rem' }}>
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-main)' }}>
                        Cronograma Detallado de Actividades
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        Intervalo oficial: {formatDateDisplay(event.start_time)} a {formatDateDisplay(event.end_time)}
                      </div>
                    </div>
                    {isCoachOrAdmin && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleToggleAddActivity}
                        style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem', minHeight: '36px' }}
                      >
                        <Plus size={15} />
                        <span>Añadir actividad</span>
                      </button>
                    )}
                  </div>

                  {/* Selector de días de la concentración */}
                  {eventDays.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedAgendaDay('all')}
                        className={`badge ${selectedAgendaDay === 'all' ? 'badge-primary' : 'badge-muted'}`}
                        style={{ cursor: 'pointer', padding: '0.35rem 0.65rem', fontSize: '0.78rem' }}
                      >
                        Todos los días ({activities.length})
                      </button>
                      {eventDays.map((d) => {
                        const dayCount = activities.filter((a) => {
                          const actDate = a.start_time.split('T')[0]
                          const actLocal = new Date(a.start_time)
                          const pad = (n: number) => String(n).padStart(2, '0')
                          const lDate = `${actLocal.getFullYear()}-${pad(actLocal.getMonth() + 1)}-${pad(actLocal.getDate())}`
                          return actDate === d.dateStr || lDate === d.dateStr
                        }).length

                        return (
                          <button
                            key={d.dateStr}
                            type="button"
                            onClick={() => setSelectedAgendaDay(d.dateStr)}
                            className={`badge ${selectedAgendaDay === d.dateStr ? 'badge-primary' : 'badge-muted'}`}
                            style={{ cursor: 'pointer', padding: '0.35rem 0.65rem', fontSize: '0.78rem' }}
                          >
                            {d.label} ({dayCount})
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {/* Formulario añadir actividad */}
                  {showAddActivity && isCoachOrAdmin && (
                    <form
                      onSubmit={handleCreateActivity}
                      style={{
                        padding: '1rem',
                        backgroundColor: 'var(--surface-subtle)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--surface-border)',
                        marginBottom: '1.25rem',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                          Nueva Actividad en Agenda
                        </div>
                        {selectedAgendaDay !== 'all' && (
                          <span className="badge badge-muted" style={{ fontSize: '0.74rem' }}>
                            Día propuesto: {selectedAgendaDay}
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                        <div>
                          <label className="form-label">Título</label>
                          <input
                            type="text"
                            required
                            className="form-input"
                            value={newActTitle}
                            onChange={(e) => setNewActTitle(e.target.value)}
                            placeholder="Ej. Meeting Ofensivo"
                          />
                        </div>
                        <div>
                          <label className="form-label">Tipo de actividad</label>
                          <select
                            className="form-input"
                            value={newActType}
                            onChange={(e) => setNewActType(e.target.value as CampActivity['activity_type'])}
                          >
                            <option value="practice">Entrenamiento / Campo</option>
                            <option value="meeting">Meeting / Reunión</option>
                            <option value="meal">Comida / Almuerzo</option>
                            <option value="rest">Descanso</option>
                            <option value="transit">Traslado / Llegadas</option>
                            <option value="other">Otro</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Inicio (Límite concentración)</label>
                          <input
                            type="datetime-local"
                            required
                            min={toDatetimeLocal(event.start_time)}
                            max={toDatetimeLocal(event.end_time)}
                            className="form-input"
                            value={newActStartTime}
                            onChange={(e) => setNewActStartTime(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="form-label">Fin (Límite concentración)</label>
                          <input
                            type="datetime-local"
                            required
                            min={toDatetimeLocal(event.start_time)}
                            max={toDatetimeLocal(event.end_time)}
                            className="form-input"
                            value={newActEndTime}
                            onChange={(e) => setNewActEndTime(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="form-label">Ubicación</label>
                          <input
                            type="text"
                            className="form-input"
                            value={newActLocation}
                            onChange={(e) => setNewActLocation(e.target.value)}
                            placeholder="Ej. Sala A o Campo 1"
                          />
                        </div>
                        <div>
                          <label className="form-label">Responsables</label>
                          <input
                            type="text"
                            className="form-input"
                            value={newActResponsibles}
                            onChange={(e) => setNewActResponsibles(e.target.value)}
                            placeholder="Ej. Christian / Alicia"
                          />
                        </div>
                        <div>
                          <label className="form-label">Unidad / Destinatarios</label>
                          <select
                            className="form-input"
                            value={newActUnit}
                            onChange={(e) => setNewActUnit(e.target.value as CampActivity['target_unit'])}
                          >
                            <option value="all">Todo el equipo</option>
                            <option value="offense">Ataque</option>
                            <option value="defense">Defensa</option>
                            <option value="staff">Solo Staff (Exclusivo)</option>
                          </select>
                        </div>
                      </div>

                      {/* Mensaje de error si la actividad queda fuera del intervalo */}
                      {isActDateOutside && (
                        <div
                          style={{
                            padding: '0.6rem 0.75rem',
                            backgroundColor: 'var(--status-danger-bg)',
                            color: 'var(--status-danger-text)',
                            border: '1px solid var(--status-danger-border)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: '0.8rem',
                            marginBottom: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                          }}
                        >
                          <AlertCircle size={15} style={{ flexShrink: 0 }} />
                          <span>
                            La actividad debe estar comprendida dentro del intervalo de la concentración ({formatDateDisplay(event.start_time)} a {formatDateDisplay(event.end_time)}).
                          </span>
                        </div>
                      )}

                      <div style={{ marginBottom: '0.75rem' }}>
                        <label className="form-label">Notas compartidas (visibles para deportistas)</label>
                        <input
                          type="text"
                          className="form-input"
                          value={newActSharedNotes}
                          onChange={(e) => setNewActSharedNotes(e.target.value)}
                          placeholder="Indicaciones para los participantes"
                        />
                      </div>

                      <div style={{ marginBottom: '0.75rem' }}>
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Lock size={13} style={{ color: 'var(--brand-yellow)' }} />
                          <span>Notas internas confidenciales (solo staff técnico)</span>
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          value={newActInternalNotes}
                          onChange={(e) => setNewActInternalNotes(e.target.value)}
                          placeholder="Anotaciones tácticas reservadas de entrenadores"
                        />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowAddActivity(false)}
                          style={{ fontSize: '0.8rem' }}
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          style={{ fontSize: '0.8rem' }}
                          disabled={isSubmittingActivity || isActDateOutside}
                        >
                          {isSubmittingActivity ? 'Guardando...' : 'Guardar Actividad'}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Lista de actividades de agenda */}
                  {filteredActivities.length === 0 ? (
                    <div className="empty-state">
                      <Clock size={32} className="empty-state-icon" />
                      <div className="empty-state-title">Sin actividades en este día</div>
                      <div className="empty-state-text">
                        {activities.length > 0
                          ? 'No hay actividades registradas para el día seleccionado. Puedes añadir una o seleccionar "Todos los días".'
                          : 'Aún no se han programado bloques horarios para este evento.'}
                      </div>
                    </div>
                  ) : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th style={{ width: '130px' }}>Horario</th>
                            <th>Actividad</th>
                            <th>Unidad</th>
                            <th>Lugar / Responsable</th>
                            {isCoachOrAdmin && <th style={{ width: '50px' }}></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredActivities.map((act) => (
                            <tr key={act.id}>
                              <td className="numeric" style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                {new Date(act.start_time).toLocaleTimeString('es-ES', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: event.timezone,
                                })}
                                {' - '}
                                {new Date(act.end_time).toLocaleTimeString('es-ES', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: event.timezone,
                                })}
                              </td>
                              <td>
                                <div style={{ fontWeight: 600 }}>{act.title}</div>
                                {act.shared_notes && (
                                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                    {act.shared_notes}
                                  </div>
                                )}
                                {act.internal_notes && isStaff && (
                                  <div
                                    style={{
                                      marginTop: '0.25rem',
                                      fontSize: '0.74rem',
                                      color: 'var(--status-warning-text)',
                                      backgroundColor: 'var(--status-warning-bg)',
                                      border: '1px solid var(--status-warning-border)',
                                      borderRadius: '3px',
                                      padding: '0.2rem 0.4rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.3rem',
                                    }}
                                  >
                                    <Lock size={11} />
                                    <span>Staff: {act.internal_notes}</span>
                                  </div>
                                )}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    act.target_unit === 'offense'
                                      ? 'badge-unit-offense'
                                      : act.target_unit === 'defense'
                                        ? 'badge-unit-defense'
                                        : act.target_unit === 'staff'
                                          ? 'badge-unit-staff'
                                          : 'badge-muted'
                                  }`}
                                  style={{ textTransform: 'capitalize' }}
                                >
                                  {act.target_unit === 'all'
                                    ? 'Todos'
                                    : act.target_unit === 'offense'
                                      ? 'Ataque'
                                      : act.target_unit === 'defense'
                                        ? 'Defensa'
                                        : 'Staff'}
                                </span>
                              </td>
                              <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                {act.location && <div>{act.location}</div>}
                                {act.responsibles && (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '0.74rem' }}>
                                    Resp: {act.responsibles}
                                  </div>
                                )}
                              </td>
                              {isCoachOrAdmin && (
                                <td>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteActivity(act.id)}
                                    style={{ color: 'var(--status-danger-text)', padding: '0.25rem' }}
                                    title="Eliminar actividad"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 4: SESIONES DE CAMPO */}
              {activeTab === 'sessions' && (
                <div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '1rem' }}>
                    Sesiones de Entrenamiento Vinculadas ({sessions.length})
                  </div>

                  {sessions.length === 0 ? (
                    <div className="empty-state">
                      <Layers size={32} className="empty-state-icon" />
                      <div className="empty-state-title">Sin sesiones vinculadas</div>
                      <div className="empty-state-text">
                        No hay sesiones de campo planificadas para este evento.
                      </div>
                    </div>
                  ) : (
                    sessions.map((sess) => (
                      <div
                        key={sess.id}
                        className="card"
                        style={{ marginBottom: '1.25rem', border: '1px solid var(--surface-border)' }}
                      >
                        <div className="card-header">
                          <div>
                            <span className="badge badge-red" style={{ marginBottom: '0.25rem' }}>
                              {sess.target_unit === 'all' ? 'Sesión Conjunta' : sess.target_unit === 'offense' ? 'Unidad: Ataque' : sess.target_unit === 'defense' ? 'Unidad: Defensa' : `Unidad: ${sess.target_unit}`}
                            </span>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
                              {sess.title}
                            </h3>
                          </div>
                          {isCoachOrAdmin && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleDuplicateSession(sess.id)}
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.65rem', minHeight: '32px' }}
                              title="Duplicar planificación para otra fecha"
                            >
                              <Copy size={13} />
                              <span>Duplicar sesión</span>
                            </button>
                          )}
                        </div>

                        {sess.objectives && (
                          <div style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', marginBottom: '0.85rem' }}>
                            <strong>Objetivos:</strong> {sess.objectives}
                          </div>
                        )}

                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem' }}>
                          Responsables: <strong>{sess.responsibles}</strong>
                        </div>

                        {/* Bloques de la sesión */}
                        {sess.blocks && sess.blocks.length > 0 && (
                          <div className="table-container" style={{ margin: 0 }}>
                            <table className="data-table" style={{ fontSize: '0.82rem' }}>
                              <thead>
                                <tr>
                                  <th style={{ width: '70px' }}>Hora</th>
                                  <th style={{ width: '60px' }}>Dur.</th>
                                  <th>Bloque / Contenido</th>
                                  <th>Tipo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sess.blocks.map((blk) => (
                                  <tr key={blk.id}>
                                    <td className="numeric" style={{ fontWeight: 600 }}>
                                      {blk.start_time}
                                    </td>
                                    <td className="numeric">{blk.duration_minutes} min</td>
                                    <td>
                                      <div style={{ fontWeight: 600 }}>{blk.title}</div>
                                      {blk.notes && (
                                        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                                          {blk.notes}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                        <span
                                          className={`badge ${blk.block_type === 'team' ? 'badge-red' : 'badge-muted'}`}
                                          style={{ textTransform: 'capitalize' }}
                                        >
                                          {blk.block_type}
                                        </span>
                                        {blk.block_type === 'team' && isCoach && (
                                          <>
                                            <button
                                              type="button"
                                              className="btn btn-secondary"
                                              style={{ fontSize: '0.74rem', padding: '0.2rem 0.55rem', minHeight: '26px' }}
                                              onClick={() => handleOpenTeamScript(sess, blk)}
                                              title="Preparar o consultar guion de repeticiones para este bloque Team"
                                            >
                                              <Layers size={12} />
                                              <span>Guion Team</span>
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-primary"
                                              style={{
                                                fontSize: '0.74rem',
                                                padding: '0.2rem 0.55rem',
                                                minHeight: '26px',
                                                backgroundColor: 'var(--brand-red)',
                                              }}
                                              onClick={() =>
                                                setFieldCaptureModalTarget({
                                                  sessionId: sess.id,
                                                  sessionTitle: sess.title,
                                                  blockId: blk.id,
                                                  blockTitle: blk.title,
                                                })
                                              }
                                              title="Abrir consola móvil de captura en campo para este bloque Team"
                                            >
                                              <Radio size={12} />
                                              <span>Capturar</span>
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'matches' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-main)' }}>
                        Partidos Asociados ({childMatches.length})
                      </h4>
                      <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                        Partidos oficiales y de preparación correspondientes a esta concentración.
                      </p>
                    </div>
                  </div>

                  {childMatches.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: '#64748b' }}>
                      <Trophy size={36} style={{ margin: '0 auto 0.75rem', opacity: 0.4 }} />
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>No hay partidos asociados registrados para esta concentración.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {childMatches.map((m) => {
                        const cap = teamCaptures.find((c) => c.event_id === m.id)
                        const snapCount = cap?.snapCount ?? cap?.snap_count ?? 0
                        const isMatchLoading = loadingHistoryId === cap?.id

                        return (
                          <div
                            key={m.id}
                            style={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: 'var(--radius-md)',
                              padding: '1.25rem',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                  <span className="badge badge-purple" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>
                                    Partido
                                  </span>
                                  <span
                                    className={`badge ${m.status === 'cancelled' ? 'badge-red' : new Date(m.end_time) < new Date() ? 'badge-green' : 'badge-gray'}`}
                                    style={{ fontSize: '0.72rem' }}
                                  >
                                    {m.status === 'cancelled' ? 'Cancelado' : new Date(m.end_time) < new Date() ? 'Disputado' : 'Programado'}
                                  </span>
                                </div>
                                <h5 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-main)' }}>
                                  {m.title}
                                </h5>
                              </div>

                              {cap && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span
                                    className="badge badge-blue"
                                    style={{
                                      fontSize: '0.8rem',
                                      fontVariantNumeric: 'tabular-nums',
                                      padding: '0.3rem 0.6rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {snapCount} snaps registrados
                                  </span>
                                </div>
                              )}
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.82rem', color: '#64748b', marginBottom: '1rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                <Calendar size={14} />
                                <span>
                                  {new Date(m.start_time).toLocaleDateString('es-ES', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                  })}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                <Clock size={14} />
                                <span>
                                  {new Date(m.start_time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} -{' '}
                                  {new Date(m.end_time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              {m.location && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                  <MapPin size={14} />
                                  <span>{m.location}</span>
                                </div>
                              )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '0.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem' }}>
                              {cap && snapCount > 0 && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenMatchHistory(cap)}
                                  disabled={isMatchLoading}
                                  className="btn btn-primary"
                                  style={{ fontSize: '0.82rem', padding: '0.4rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                                >
                                  <Trophy size={15} />
                                  <span>{isMatchLoading ? 'Cargando jugadas...' : 'Ver snaps (Historial)'}</span>
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const url = new URL(window.location.href)
                                  url.searchParams.set('view', 'sports')
                                  url.searchParams.set('sportsTab', 'stats')
                                  window.location.href = url.toString()
                                }}
                                className="btn btn-secondary"
                                style={{ fontSize: '0.82rem', padding: '0.4rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                              >
                                <BarChart2 size={15} />
                                <span>Ver en Estadísticas</span>
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Pie del Modal */}
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" data-evaluation-leave onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>

      {/* Modal de Convocatoria */}
      {showManageRosterModal && event && (
        <ManageRosterModal
          event={event}
          onClose={() => setShowManageRosterModal(false)}
          onSuccess={(summary) => {
            setSuccessMsg(summary)
            loadEventData()
            if (onEventUpdated) onEventUpdated()
          }}
        />
      )}

      {/* Modal de Guion Team (M3) */}
      {selectedScriptTarget && event && (
        <ScriptEditorModal
          isOpen={true}
          onClose={() => setSelectedScriptTarget(null)}
          scriptId={selectedScriptTarget.scriptId}
          sessionId={selectedScriptTarget.sessionId}
          sessionTitle={selectedScriptTarget.sessionTitle}
          blockId={selectedScriptTarget.blockId}
          blockTitle={selectedScriptTarget.blockTitle}
          eventTitle={event.title}
          teamId={event.team_id}
          onSaved={() => {
            setSelectedScriptTarget(null)
            setSuccessMsg('Guion de repeticiones guardado correctamente')
          }}
        />
      )}

      {/* Modal de Entrada a Captura en Campo (M4) */}
      {fieldCaptureModalTarget && event && (
        <FieldCaptureEntryModal
          isOpen={true}
          onClose={() => setFieldCaptureModalTarget(null)}
          teamId={event.team_id}
          blockId={fieldCaptureModalTarget.blockId}
          blockTitle={fieldCaptureModalTarget.blockTitle}
          sessionId={fieldCaptureModalTarget.sessionId}
          sessionTitle={fieldCaptureModalTarget.sessionTitle}
          onLaunchCapture={(capture: TeamBlockCapture) => {
            setActiveCaptureId(capture.id)
            setFieldCaptureModalTarget(null)
          }}
        />
      )}

      {/* Consola Móvil de Captura en Campo (M4) */}
      {activeCaptureId && (
        <FieldConsoleView
          captureId={activeCaptureId}
          onClose={() => {
            setActiveCaptureId(null)
            loadEventData()
          }}
        />
      )}

      {/* Modal de Historial de Jugadas (M4/M7) */}
      {historyModalOpen && selectedHistoryCapture && (
        <SnapHistoryModal
          isOpen={historyModalOpen}
          onClose={() => {
            setHistoryModalOpen(false)
            setSelectedHistoryCapture(null)
            setHistorySnaps([])
          }}
          capture={selectedHistoryCapture}
          snaps={historySnaps}
          onSelectSnap={(snap) => {
            setSelectedDetailSnap(snap)
            setPastSnapDetailOpen(true)
          }}
        />
      )}

      {/* Modal de Detalle de Snap (M4/M7) */}
      {pastSnapDetailOpen && selectedDetailSnap && (
        <PastSnapDetailModal
          isOpen={pastSnapDetailOpen}
          onClose={() => {
            setPastSnapDetailOpen(false)
            setSelectedDetailSnap(null)
          }}
          snap={selectedDetailSnap}
          onCorrectResult={async (params) => {
            const updated = await fieldCaptureService.correctSnapResult({
              snapId: params.snapId,
              newPrimaryResult: params.newPrimaryResult,
              passerAthleteId: params.passerId,
              receiverAthleteId: params.receiverId,
              tacklerAthleteId: params.tacklerId,
            })
            setHistorySnaps((prev) => prev.map((s) => (s.id === params.snapId ? updated : s)))
            setSelectedDetailSnap(updated)
          }}
          onVoidSnap={async (snapId, validity, reason) => {
            await fieldCaptureService.voidSnap(snapId, validity, reason)
            setHistorySnaps((prev) =>
              prev.map((s) => (s.id === snapId ? { ...s, validity, review_notes: reason } : s))
            )
            setSelectedDetailSnap((prev) => (prev && prev.id === snapId ? { ...prev, validity, review_notes: reason } : prev))
          }}
        />
      )}
    </div>
  )
}
